import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiCall } from '../utils/api';
import config from '../config/appConfig';
import useUserPermissions from '../hooks/useUserPermissions';
import * as XLSX from 'xlsx';
function ImportRunbook() {
    const navigate = useNavigate();
    const { canModify } = useUserPermissions();
    const [formData, setFormData] = useState({
        tribe: '',
        solution: '',
        runbookDescription: '',
        tags: '',
        excelFile: null
    });
    const [tribes, setTribes] = useState([]);
    const [allSolutions, setAllSolutions] = useState({});
    const [solutions, setSolutions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [createdRunbookId, setCreatedRunbookId] = useState(null);
    const [showRunbookDetails, setShowRunbookDetails] = useState(false);
    const [runbookData, setRunbookData] = useState(null);
    const [uploadingImages, setUploadingImages] = useState({});
    const [imageUrls, setImageUrls] = useState({});

    // Load tribes and solutions from config
    useEffect(() => {
        try {
            setTribes(config.tribes || []);
            setAllSolutions(config.solutions || {});
        } catch (err) {
            console.error('Failed to load config:', err);
            setError('Failed to load configuration');
        }
    }, []);

    // Update solutions when tribe changes
    useEffect(() => {
        if (formData.tribe && allSolutions[formData.tribe]) {
            setSolutions(Object.keys(allSolutions[formData.tribe]));
        } else {
            setSolutions([]);
        }
        // Reset solution when tribe changes
        if (formData.tribe) {
            setFormData(prev => ({ ...prev, solution: '' }));
        }
    }, [formData.tribe, allSolutions]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setError('');
    };

    //----------------------------------
    //New Code for Excel file validation
    //----------------------------------  
 
    const REQUIRED_HEADERS = [
    'TRIBE',
    'SOLUTION',
    'RUNBOOK DESCRIPTION',
    'RESOLUTION STEPS',
    'TAGS'
    ];
 
    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
 
        try {
            // Extension check
            if (!/\.(xlsx|xls)$/i.test(file.name)) {
                throw new Error('Only Excel files (.xlsx, .xls) are allowed');
            }
 
            // Parse Excel
            const buffer = await file.arrayBuffer();
            const workbook = XLSX.read(buffer, { type: 'array' });
 
            // Get first sheet
            const sheetName = workbook.SheetNames[0];
            if (!sheetName) {
                throw new Error('Excel file is empty');
            }
 
            const sheet = workbook.Sheets[sheetName];
 
            // Read header row only
            const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
            if (!rows.length) {
                throw new Error('Excel file has no header row');
            }
 
            const headers = rows[0]
                .map(h => String(h).trim().toUpperCase())
                .filter(Boolean);
 
            // Case-insensitive header validation
            const missingHeaders = REQUIRED_HEADERS.filter(
                h => !headers.includes(h)
            );
 
            if (missingHeaders.length > 0) {
                throw new Error(
                    `Missing required headers: ${missingHeaders.join(', ')}`
                );
            }
 
            // Accept file
            setFormData(prev => ({
                ...prev,
                excelFile: file
            }));
            setError('');
 
        } catch (err) {
            setError(err.message || 'Invalid Excel file');
            e.target.value = '';
        }
    };


    // const handleFileChange = (e) => {
    //     const file = e.target.files[0];
    //     if (file) {
    //         // Validate file type
    //         if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
    //             setError('Please upload a valid Excel file (.xlsx or .xls)');
    //             e.target.value = '';
    //             return;
    //         }
    //         setFormData(prev => ({
    //             ...prev,
    //             excelFile: file
    //         }));
    //         setError('');
    //     }
    // };

    const handleCreateRunbook = async (e) => {
        e.preventDefault();
        
        // Validation
        if (!formData.tribe) {
            setError('Please select a Tribe');
            return;
        }
        if (!formData.solution) {
            setError('Please select a Solution');
            return;
        }
        if (!formData.runbookDescription.trim()) {
            setError('Please enter a Runbook Description');
            return;
        }
        if (formData.runbookDescription.length > 256) {
            setError('Runbook Description must not exceed 256 characters');
            return;
        }
        if (!formData.excelFile) {
            setError('Please upload an Excel file');
            return;
        }

        try {
            setLoading(true);
            setError('');
            
            // Read file as base64
            const reader = new FileReader();
            reader.onload = async () => {
                try {
                    const base64File = reader.result.split(',')[1]; // Remove data:... prefix
                    
                    // Send everything in one request
                    const requestData = {
                        tribe: formData.tribe,
                        solution: formData.solution,
                        runbookTitle: formData.runbookDescription,
                        tags: formData.tags,
                        file: base64File
                    };

                    const response = await apiCall('/runbooks/import-excel', {
                        method: 'POST',
                        body: JSON.stringify(requestData)
                    });

                    setCreatedRunbookId(response.runbookId);
                    setSuccess(`Runbook imported successfully! ID: ${response.runbookId}, Steps: ${response.stepsCount}`);
                    setLoading(false);
                    
                    // Don't auto-redirect anymore, let user click "Show Runbook" button
                    
                } catch (err) {
                    console.error('Failed to import runbook:', err);
                    setError(err.message || 'Failed to import runbook');
                    setLoading(false);
                }
            };
            
            reader.onerror = () => {
                setError('Failed to read Excel file');
                setLoading(false);
            };
            
            reader.readAsDataURL(formData.excelFile);
            
        } catch (err) {
            console.error('Failed to import runbook:', err);
            setError(err.message || 'Failed to import runbook');
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate('/runbook_list');
    };

    const handleShowRunbook = async () => {
        if (!createdRunbookId) return;
        
        try {
            setLoading(true);
            // Fetch the runbook details from draft table
            const data = await apiCall(`/runbooks/drafts/${createdRunbookId}`);
            setRunbookData(data.runbook);
            setShowRunbookDetails(true);
            setLoading(false);
        } catch (err) {
            console.error('Failed to fetch runbook:', err);
            setError(err.message || 'Failed to load runbook details');
            setLoading(false);
        }
    };

    const handleImageUpload = async (stepNumber, file) => {
        if (!file || !createdRunbookId || !runbookData) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setError('Please upload a valid image file');
            return;
        }

        try {
            setUploadingImages(prev => ({ ...prev, [stepNumber]: true }));
            setError('');

            // Read file as base64
            const reader = new FileReader();
            reader.onload = async () => {
                try {
                    const base64Image = reader.result.split(',')[1];

                    // Send to backend for S3 upload
                    const uploadData = {
                        runbookId: createdRunbookId,
                        tribe: runbookData.Tribe,
                        solution: runbookData.Solution,
                        stepNumber: stepNumber,
                        image: base64Image,
                        fileName: file.name
                    };

                    const response = await apiCall('/runbooks/upload-step-image', {
                        method: 'POST',
                        body: JSON.stringify(uploadData)
                    });

                    // Update the imageUrls state with the presigned URL
                    setImageUrls(prev => ({ ...prev, [stepNumber]: response.imageUrl }));
                    
                    // Update the runbook data to include BOTH image_s3_key and image_url
                    setRunbookData(prev => {
                        const updatedSteps = prev.Steps.map(step => {
                            if (step.step_number === stepNumber) {
                                return { 
                                    ...step, 
                                    image_s3_key: response.s3Key,      // Store S3 key for DynamoDB
                                    image_url: response.imageUrl        // Store presigned URL for display
                                };
                            }
                            return step;
                        });
                        return { ...prev, Steps: updatedSteps };
                    });

                    setUploadingImages(prev => ({ ...prev, [stepNumber]: false }));
                    alert(`Image uploaded successfully for Step ${stepNumber}!`);
                } catch (err) {
                    console.error('Failed to upload image:', err);
                    setError(err.message || 'Failed to upload image');
                    setUploadingImages(prev => ({ ...prev, [stepNumber]: false }));
                }
            };

            reader.onerror = () => {
                setError('Failed to read image file');
                setUploadingImages(prev => ({ ...prev, [stepNumber]: false }));
            };

            reader.readAsDataURL(file);
        } catch (err) {
            console.error('Failed to upload image:', err);
            setError(err.message || 'Failed to upload image');
            setUploadingImages(prev => ({ ...prev, [stepNumber]: false }));
        }
    };

    const handleFinalize = async () => {
        // Redirect based on user permissions
        if (canModify()) {
            navigate('/draft-runbooks');
        } else {
            navigate('/runbook_list');
        }
    };

    return (
        <div className="container-fluid mt-4">
            {!showRunbookDetails ? (
                /* Import Form */
                <div className="card shadow">
                    <div className="card-header bg-primary text-white">
                        <h3 className="mb-0">Import Runbook from Excel</h3>
                    </div>
                    <div className="card-body">
                    {error && (
                        <div className="alert alert-danger alert-dismissible fade show" role="alert">
                            {error}
                            <button type="button" className="btn-close" onClick={() => setError('')}></button>
                        </div>
                    )}

                    {success && (
                        <div className="alert alert-success alert-dismissible fade show" role="alert">
                            {success}
                            <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
                        </div>
                    )}

                    <form onSubmit={handleCreateRunbook}>
                        <div className="row">
                            {/* Runbook ID */}
                            <div className="col-md-6 mb-3">
                                <label className="form-label fw-bold">
                                    Runbook ID
                                </label>
                                <input
                                    type="text"
                                    className="form-control bg-light"
                                    value={createdRunbookId || 'Auto Generated'}
                                    disabled
                                />
                                <small className="text-muted">example - RD_OSFM_123</small>
                            </div>

                            {/* Tribe */}
                            <div className="col-md-6 mb-3">
                                <label className="form-label fw-bold">
                                    Tribe <span className="text-danger">*</span>
                                </label>
                                <select
                                    name="tribe"
                                    className="form-select"
                                    value={formData.tribe}
                                    onChange={handleInputChange}
                                    required
                                    disabled={createdRunbookId}
                                >
                                    <option value="">Select Tribe</option>
                                    {tribes.map(tribe => (
                                        <option key={tribe} value={tribe}>{tribe}</option>
                                    ))}
                                </select>
                                <small className="text-muted">From config file</small>
                            </div>
                        </div>

                        <div className="row">
                            {/* Solution */}
                            <div className="col-md-6 mb-3">
                                <label className="form-label fw-bold">
                                    Solution <span className="text-danger">*</span>
                                </label>
                                <select
                                    name="solution"
                                    className="form-select"
                                    value={formData.solution}
                                    onChange={handleInputChange}
                                    required
                                    disabled={createdRunbookId}
                                >
                                    <option value="">Select Solution</option>
                                    {solutions.map(solution => (
                                        <option key={solution} value={solution}>{solution}</option>
                                    ))}
                                </select>
                                <small className="text-muted">From config file</small>
                            </div>

                            {/* Modified By */}
                            <div className="col-md-6 mb-3">
                                <label className="form-label fw-bold">
                                    Modified by
                                </label>
                                <input
                                    type="text"
                                    className="form-control bg-light"
                                    value="Logged in User"
                                    disabled
                                />
                            </div>
                        </div>

                        <div className="row">
                            {/* Runbook Description */}
                            <div className="col-md-12 mb-3">
                                <label className="form-label fw-bold">
                                    Runbook Description <span className="text-danger">*</span>
                                </label>
                                <textarea
                                    name="runbookDescription"
                                    className="form-control"
                                    rows="3"
                                    maxLength="256"
                                    value={formData.runbookDescription}
                                    onChange={handleInputChange}
                                    placeholder="Enter runbook description (max 256 characters)"
                                    required
                                    disabled={createdRunbookId}
                                />
                                <small className="text-muted">
                                    {formData.runbookDescription.length}/256 characters
                                </small>
                            </div>
                        </div>

                        <div className="row">
                            {/* Modified Date */}
                            <div className="col-md-6 mb-3">
                                <label className="form-label fw-bold">
                                    Modified Date
                                </label>
                                <input
                                    type="text"
                                    className="form-control bg-light"
                                    value={new Date().toISOString().slice(0, 19).replace('T', ' ')}
                                    disabled
                                />
                                <small className="text-muted">YYYY-MM-DD HH:MM:SS</small>
                            </div>

                            {/* Tags */}
                            <div className="col-md-6 mb-3">
                                <label className="form-label fw-bold">
                                    Tags
                                </label>
                                <input
                                    type="text"
                                    name="tags"
                                    className="form-control"
                                    value={formData.tags}
                                    onChange={handleInputChange}
                                    placeholder="tag1, tag2, tag3"
                                    disabled={createdRunbookId}
                                />
                                <small className="text-muted">Comma separated values</small>
                            </div>
                        </div>

                        <div className="row">
                            {/* Upload Excel */}
                            <div className="col-md-12 mb-4">
                                <label className="form-label fw-bold">
                                    Upload Excel File <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="file"
                                    className="form-control"
                                    accept=".xlsx,.xls"
                                    onChange={handleFileChange}
                                    required
                                    disabled={createdRunbookId}
                                />
                                <small className="text-muted">
                                    Upload Excel file with runbook modules and steps (Required)
                                </small>
                            </div>
                        </div>

                        <hr className="my-4" />

                        {/* Action Buttons */}
                        <div className="d-flex gap-2 justify-content-end">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={handleCancel}
                                disabled={loading}
                            >
                                Cancel
                            </button>

                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={loading || createdRunbookId}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                        Importing...
                                    </>
                                ) : createdRunbookId ? (
                                    'Import Complete'
                                ) : (
                                    'Import Runbook'
                                )}
                            </button>

                            {createdRunbookId && (
                                <button
                                    type="button"
                                    className="btn btn-success"
                                    onClick={handleShowRunbook}
                                    disabled={loading}
                                >
                                    Show Runbook
                                </button>
                            )}
                        </div>

                        {createdRunbookId && (
                            <div className="alert alert-success mt-3" role="alert">
                                <i className="bi bi-check-circle me-2"></i>
                                Runbook imported successfully! Click "Show Runbook" to add images to steps.
                            </div>
                        )}
                    </form>
                </div>
            </div>
            ) : (
                /* Runbook Details with Image Upload */
                <div className="card shadow">
                    <div className="card-header bg-success text-white d-flex justify-content-between align-items-center">
                        <h3 className="mb-0">Runbook Details - {runbookData?.Runbook_ID}</h3>
                        <button className="btn btn-light btn-sm" onClick={() => setShowRunbookDetails(false)}>
                            Back to Form
                        </button>
                    </div>
                    <div className="card-body">
                        {error && (
                            <div className="alert alert-danger alert-dismissible fade show" role="alert">
                                {error}
                                <button type="button" className="btn-close" onClick={() => setError('')}></button>
                            </div>
                        )}

                        <div className="row mb-4">
                            <div className="col-md-6">
                                <p><strong>Runbook ID:</strong> {runbookData?.Runbook_ID}</p>
                                <p><strong>Title:</strong> {runbookData?.Title}</p>
                                <p><strong>Tribe:</strong> {runbookData?.Tribe}</p>
                            </div>
                            <div className="col-md-6">
                                <p><strong>Solution:</strong> {runbookData?.Solution}</p>
                                <p><strong>Tags:</strong> {runbookData?.Tags?.join(', ') || 'None'}</p>
                                <p><strong>Total Steps:</strong> {runbookData?.Steps?.length || 0}</p>
                            </div>
                        </div>

                        <hr />

                        <h4 className="mb-3">Steps (Upload Images - Optional)</h4>
                        
                        {runbookData?.Steps?.map((step, index) => (
                            <div key={step.step_number} className="card mb-3">
                                <div className="card-header bg-light">
                                    <h5 className="mb-0">Step {step.step_number}</h5>
                                </div>
                                <div className="card-body">
                                    <p className="mb-3">{step.step_description}</p>
                                    
                                    <div className="row align-items-center">
                                        <div className="col-md-6">
                                            <label className="form-label fw-bold">
                                                Upload Image (Optional)
                                            </label>
                                            <input
                                                type="file"
                                                className="form-control"
                                                accept="image/*"
                                                onChange={(e) => {
                                                    const file = e.target.files[0];
                                                    if (file) handleImageUpload(step.step_number, file);
                                                }}
                                                disabled={uploadingImages[step.step_number]}
                                            />
                                            <small className="text-muted">
                                                Folder: {runbookData.Tribe}/{runbookData.Solution}/{runbookData.Runbook_ID}/step_{step.step_number}
                                            </small>
                                        </div>
                                        <div className="col-md-6">
                                            {uploadingImages[step.step_number] && (
                                                <div className="text-primary">
                                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                                    Uploading...
                                                </div>
                                            )}
                                            {imageUrls[step.step_number] && (
                                                <div className="alert alert-success mb-0">
                                                    <i className="bi bi-check-circle me-2"></i>
                                                    Image uploaded successfully!
                                                    <div className="mt-3">
                                                        <img 
                                                            src={imageUrls[step.step_number]} 
                                                            alt={`Step ${step.step_number}`}
                                                            className="img-fluid rounded border"
                                                            style={{ maxHeight: '300px', maxWidth: '100%', objectFit: 'contain' }}
                                                        />
                                                    </div>
                                                    <div className="mt-2">
                                                        <a href={imageUrls[step.step_number]} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
                                                            <i className="bi bi-box-arrow-up-right me-1"></i>
                                                            View Full Size
                                                        </a>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}

                        <hr className="my-4" />

                        <div className="d-flex gap-2 justify-content-end">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={handleCancel}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleFinalize}
                            >
                                Import as Draft
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ImportRunbook;
